import { defineStore } from "pinia";
import { ref } from "vue";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-vue";
import { showLoadingIndicator } from "@/utils/loadingState.js";

const SUPPORTED_RESOURCES = ["companies", "physical-persons"];

const parseFilenameFromContentDisposition = (header) => {
  if (!header) return null;
  const utf8Match = header.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1].trim());
    } catch {
      return null;
    }
  }
  const quotedMatch = header.match(/filename\s*=\s*"([^"]+)"/i);
  if (quotedMatch) return quotedMatch[1];
  const bareMatch = header.match(/filename\s*=\s*([^;]+)/i);
  if (bareMatch) return bareMatch[1].trim();
  return null;
};

export const useCtifDeclarationStore = (resourceType, resourceId) => {
  if (!SUPPORTED_RESOURCES.includes(resourceType)) {
    throw new Error(`Unsupported CTIF resource type: ${resourceType}`);
  }
  const storeName = `ctifDeclaration_${resourceType}_${resourceId}`;

  return defineStore(storeName, () => {
    const apiUrl = import.meta.env.VITE_API_SERVER_URL;
    const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
    const data = ref(null);

    const authHeaders = async () => {
      try {
        const token = await getAccessTokenSilently();
        return { Authorization: `Bearer ${token}` };
      } catch {
        await loginWithRedirect();
        return {};
      }
    };

    const baseUrl = `${apiUrl}/api/${resourceType}/${resourceId}/ctif-declaration`;

    const fetchPrefill = async () => {
      showLoadingIndicator.value = true;
      try {
        const response = await axios.get(`${baseUrl}/prefill`, {
          headers: await authHeaders(),
        });
        data.value = response.data.data;
        return data.value;
      } finally {
        showLoadingIndicator.value = false;
      }
    };

    const downloadBlob = async (declaration, { path, mimeType, fallbackFilename }) => {
      showLoadingIndicator.value = true;
      try {
        const response = await axios.post(
          `${baseUrl}/${path}`,
          { declaration },
          { headers: await authHeaders(), responseType: "blob" },
        );
        const blob = new Blob([response.data], { type: mimeType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download =
          parseFilenameFromContentDisposition(response.headers["content-disposition"]) ||
          fallbackFilename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } finally {
        showLoadingIndicator.value = false;
      }
    };

    const downloadPdf = (declaration) =>
      downloadBlob(declaration, {
        path: "download",
        mimeType: "application/pdf",
        fallbackFilename: "Déclaration CTIF.pdf",
      });

    const downloadXml = (declaration) =>
      downloadBlob(declaration, {
        path: "download-xml",
        mimeType: "application/xml",
        fallbackFilename: "Déclaration CTIF.xml",
      });

    return {
      data,
      fetchPrefill,
      downloadPdf,
      downloadXml,
    };
  })();
};
